'use client';

import { useMemo, useState } from 'react';

import {
  useDeleteDeliverableTypeMutation,
  useGetDeliverableTypesQuery,
} from '@/features/deliverable-types/api/deliverable-typesApi';
import { useCanWorkspaceAction } from '@/features/workspaces/hooks';
import {
  ConfirmActionModal,
  EmptyState,
  InfiniteTable,
  Modal,
  OptionsMenu,
} from '@web/ui';
import { getDateTimeFormat, getErrorMessage, notify } from '@web/utils';

import { DeliverableTypeModalForm } from './DeliverableTypeModalForm';
import styles from './SettingsDeliverableTypesPanel.module.scss';

import type { DeliverableType } from '@/features/deliverable-types/types';

type SettingsDeliverableTypesPanelProps = {
  search: string;
};

export function SettingsDeliverableTypesPanel({
  search,
}: SettingsDeliverableTypesPanelProps) {
  const canEditDeliverableTypes = useCanWorkspaceAction(
    'deliverable_type_management',
    'edit',
  );
  const canDeleteDeliverableTypes = useCanWorkspaceAction(
    'deliverable_type_management',
    'delete',
  );

  const [editingDeliverableType, setEditingDeliverableType] =
    useState<DeliverableType | null>(null);
  const [deletingDeliverableType, setDeletingDeliverableType] =
    useState<DeliverableType | null>(null);

  const { data: deliverableTypesResponse, isFetching } =
    useGetDeliverableTypesQuery({
      page_size: 100,
      query: search || undefined,
      show_archived: false,
    });
  const [deleteDeliverableType, { isLoading: isDeleting }] =
    useDeleteDeliverableTypeMutation();

  const rows = useMemo(
    () => deliverableTypesResponse?.data?.results ?? [],
    [deliverableTypesResponse?.data?.results],
  );

  const handleDeleteDeliverableType = async () => {
    if (!deletingDeliverableType || isDeleting) {
      return;
    }

    if (!canDeleteDeliverableTypes) {
      notify({
        type: 'error',
        message: 'You do not have permission to delete deliverable types.',
      });
      return;
    }

    try {
      await deleteDeliverableType({
        deliverableTypeId: deletingDeliverableType.id,
      }).unwrap();
      notify({ type: 'success', message: 'Deliverable type deleted.' });
      setDeletingDeliverableType(null);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const columns = [
    {
      id: 'name',
      header: 'Name',
      accessor: (row: DeliverableType) => row.label,
      sortable: true,
      cell: (row: DeliverableType) => (
        <span className={styles.deliverableTypesPanel__nameCell}>
          {row.label}
        </span>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      accessor: (row: DeliverableType) => row.created_at,
      cell: (row: DeliverableType) => getDateTimeFormat(row.created_at),
      sortable: true,
    },
    {
      id: 'usage_count',
      header: 'Usage Count',
      accessor: (row: DeliverableType) => row.usage_count ?? 0,
      sortable: true,
    },
    ...(canEditDeliverableTypes || canDeleteDeliverableTypes
      ? [
          {
            id: 'actions',
            header: '',
            width: 72,
            accessor: (row: DeliverableType) => row.id,
            sortable: false,
            cell: (row: DeliverableType) => {
              const actions = [
                ...(canEditDeliverableTypes
                  ? [
                      {
                        key: `edit-${row.id}`,
                        label: 'Edit',
                        onSelect: () => setEditingDeliverableType(row),
                      },
                    ]
                  : []),
                ...(canDeleteDeliverableTypes
                  ? [
                      {
                        key: `delete-${row.id}`,
                        label: 'Delete',
                        tone: 'danger' as const,
                        onSelect: () => setDeletingDeliverableType(row),
                        loading: isDeleting,
                      },
                    ]
                  : []),
              ];

              if (!actions.length) {
                return null;
              }

              return (
                <div className={styles.deliverableTypesPanel__actionsCell}>
                  <OptionsMenu
                    ariaLabel="Deliverable type actions"
                    actions={actions}
                  />
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div className={styles.deliverableTypesPanel}>
      {!isFetching && rows.length === 0 ? (
        <EmptyState title="No deliverable types found." />
      ) : (
        <InfiniteTable<DeliverableType>
          tableId="settings-deliverable-types-table"
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          onLoadMore={() => {}}
          hasMore={false}
          isLoading={isFetching}
          virtualizedHeight="auto"
        />
      )}

      <Modal
        open={Boolean(editingDeliverableType)}
        onClose={() => setEditingDeliverableType(null)}
        width="min(720px, 96vw)"
      >
        <div className={`card-container-glass ${styles.modalCard}`}>
          <h3 className={styles.modalCard__title}>Edit deliverable type</h3>
          <p className={styles.modalCard__subtitle}>
            {editingDeliverableType?.label ?? ''}
          </p>
          <DeliverableTypeModalForm
            mode="edit"
            deliverableType={editingDeliverableType}
            onCancel={() => setEditingDeliverableType(null)}
            onSuccess={() => setEditingDeliverableType(null)}
          />
        </div>
      </Modal>

      <ConfirmActionModal
        open={Boolean(deletingDeliverableType)}
        title="Delete deliverable type?"
        description="This type is used in existing deliverables. Deleting it will not remove it from those deliverables."
        requireConfirmationText={false}
        confirmButtonLabel="Delete"
        confirmButtonVariant="primary"
        isProcessing={isDeleting}
        onConfirm={handleDeleteDeliverableType}
        onClose={() => setDeletingDeliverableType(null)}
      />
    </div>
  );
}
